/**
 * Advice scenarios — what one saved scenario holds, and how it moves between
 * the client record and the scenario store.
 *
 * The LIVE scenario of each page stays in today's top-level client fields, so
 * every document, AI, file-note and projection reader is unchanged. Other
 * scenarios are stored in `clients/{id}/adviceScenarios/{sid}` as `payload`.
 * The index (`adviceScenarios/_index`) names the live scenario of each page.
 *
 *   Recommendations payload = { recommendations, fees (all but the shared keys),
 *                               fundProjections (recommended* / alternative* / likeForLike* keys) }
 *   Amendments payload      = { amendments } — on clients with scenarios it carries its own
 *                               `amendments.fundProjections` (recommended* / alternative* keys)
 *
 * Fact Find data, `fees.ongoingConsent` (Advice History) and the current-portfolio
 * copies in `fundProjections` stay shared by every scenario.
 *
 * Plain CommonJS shared by the browser and the server — no object spread.
 */

const PAGES = ['recommendations', 'amendments'];
const MAX_SCENARIOS = 5;
const MAX_NAME_LENGTH = 40;
/** `fees` keys that belong to the client, not to a scenario. */
const SHARED_FEE_KEYS = ['ongoingConsent'];
const FP_ADVICE_RE = /^(recommended|alternative|likeForLike)/;
const SCENARIO_ID_RE = /^sc_[a-f0-9]{10}$/;
const LIVE_ALIAS = 'live';

function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
    && (Object.getPrototypeOf(value) === Object.prototype || Object.getPrototypeOf(value) === null);
}

/** Deep copy of plain objects and arrays; anything else (dates, Firestore timestamps) is kept as is. */
function cloneValue(value) {
  if (Array.isArray(value)) return value.map(cloneValue);
  if (isPlainObject(value)) {
    const out = {};
    Object.keys(value).forEach((key) => { out[key] = cloneValue(value[key]); });
    return out;
  }
  return value;
}

function isPage(page) {
  return PAGES.indexOf(page) !== -1;
}

function isFundProjectionsAdviceKey(key) {
  return FP_ADVICE_RE.test(String(key || ''));
}

function isScenarioFeeKey(key) {
  return SHARED_FEE_KEYS.indexOf(String(key)) === -1;
}

function pickKeys(source, keep) {
  const out = {};
  if (!isPlainObject(source)) return out;
  Object.keys(source).forEach((key) => {
    if (keep(key)) out[key] = cloneValue(source[key]);
  });
  return out;
}

/** The advice part of `fundProjections`. */
function fundProjectionsAdvice(fundProjections) {
  return pickKeys(fundProjections, isFundProjectionsAdviceKey);
}

/** The shared (current-portfolio) part of `fundProjections`. */
function fundProjectionsShared(fundProjections) {
  return pickKeys(fundProjections, (key) => !isFundProjectionsAdviceKey(key));
}

function scenarioFees(fees) {
  return pickKeys(fees, isScenarioFeeKey);
}

function sharedFees(fees) {
  return pickKeys(fees, (key) => !isScenarioFeeKey(key));
}

/** Whether this client's Amendments keep their own super projection fields (clients with scenarios). */
function hasOwnAmendmentsFundProjections(clientData) {
  return Boolean(clientData && clientData.amendments && isPlainObject(clientData.amendments.fundProjections));
}

/**
 * The client as the Amendments documents and page see it: on clients whose
 * Amendments keep their own super projection fields, those replace the
 * recommended / alternative keys of `fundProjections`. Otherwise unchanged.
 */
function amendmentsAdviceView(clientData) {
  if (!hasOwnAmendmentsFundProjections(clientData)) return clientData;
  const own = clientData.amendments.fundProjections;
  return Object.assign({}, clientData, {
    fundProjections: Object.assign(fundProjectionsShared(clientData.fundProjections), cloneValue(own)),
  });
}

/** The scenario payload of `page`, read from a client record (the live scenario). */
function extractScenarioPayload(page, clientData) {
  const c = clientData || {};
  if (page === 'amendments') {
    return { amendments: cloneValue(isPlainObject(c.amendments) ? c.amendments : {}) };
  }
  return {
    recommendations: cloneValue(isPlainObject(c.recommendations) ? c.recommendations : {}),
    fees: scenarioFees(c.fees),
    fundProjections: fundProjectionsAdvice(c.fundProjections),
  };
}

/** A client record with `payload` in place of the page's live scenario (for editing or previewing another scenario). */
function applyScenarioPayload(page, clientData, payload) {
  const c = clientData || {};
  const p = payload || {};
  if (page === 'amendments') {
    return Object.assign({}, c, { amendments: cloneValue(isPlainObject(p.amendments) ? p.amendments : {}) });
  }
  return Object.assign({}, c, {
    recommendations: cloneValue(isPlainObject(p.recommendations) ? p.recommendations : {}),
    fees: Object.assign(sharedFees(c.fees), scenarioFees(p.fees)),
    fundProjections: Object.assign(fundProjectionsShared(c.fundProjections), fundProjectionsAdvice(p.fundProjections)),
  });
}

/**
 * Client-doc updates that make `incomingPayload` the live scenario of `page`.
 * Whole maps are written: the caller runs this inside a transaction that read
 * `currentClient`, so the shared keys written back are the ones it read.
 */
function buildLiveSwapUpdates(page, currentClient, incomingPayload) {
  const c = currentClient || {};
  const p = incomingPayload || {};
  if (page === 'amendments') {
    return { amendments: cloneValue(isPlainObject(p.amendments) ? p.amendments : {}) };
  }
  return {
    recommendations: cloneValue(isPlainObject(p.recommendations) ? p.recommendations : {}),
    fees: Object.assign(sharedFees(c.fees), scenarioFees(p.fees)),
    fundProjections: Object.assign(fundProjectionsShared(c.fundProjections), fundProjectionsAdvice(p.fundProjections)),
  };
}

/** A value that says something: not null / '' / an empty object or array. */
function hasContent(value) {
  if (value == null || value === '') return false;
  if (Array.isArray(value)) return value.length > 0;
  if (isPlainObject(value)) return Object.keys(value).some((key) => hasContent(value[key]));
  return true;
}

/**
 * Recommendations as an Annual Review starts: nothing recorded, with super
 * change switched off, so the review reads "No change" to super until the
 * adviser switches it on (an insurance edit never infers a rollover).
 */
const CLEARED_RECOMMENDATIONS = Object.freeze({ changeSuperannuation: false });

/**
 * Client-doc updates that clear the advice layer when an Annual Review starts
 * (owner, Oct 2026): Recommendations (CLEARED_RECOMMENDATIONS), the advice keys
 * of `fundProjections` and Amendments start empty. Fees, `fees.ongoingConsent`,
 * the current-portfolio keys and the Fact Find stay. Whole maps are written: the caller runs this in a
 * transaction that read `client`. A client with a scenario store keeps its own,
 * now empty, Amendments projection copy (as a fresh store seeds it). The AR
 * document type pick (`arDocumentType`) is deleted by the caller.
 *
 *   options.hasStore   the client has a scenario store (`adviceScenarios/_index`)
 *
 * → { updates, areas } — `areas` names what held anything:
 *   'recommendations' | 'fundProjections' | 'amendments' | 'arDocumentType'
 */
function buildAdviceClearUpdates(client, options) {
  const c = client || {};
  const o = options || {};
  const areas = [];
  const recommendations = isPlainObject(c.recommendations) ? Object.assign({}, c.recommendations) : {};
  if (recommendations.changeSuperannuation === false) delete recommendations.changeSuperannuation;
  if (hasContent(recommendations)) areas.push('recommendations');
  if (hasContent(fundProjectionsAdvice(c.fundProjections))) areas.push('fundProjections');
  if (hasContent(c.amendments)) areas.push('amendments');
  if (hasContent(c.arDocumentType)) areas.push('arDocumentType');
  return {
    updates: {
      recommendations: cloneValue(CLEARED_RECOMMENDATIONS),
      fundProjections: fundProjectionsShared(c.fundProjections),
      amendments: o.hasStore ? { fundProjections: {} } : {},
    },
    areas,
  };
}

/**
 * Split a page's autosave updates (dot paths from buildClientUpdateDiff) into
 * paths that belong to the open scenario and paths shared by the client.
 *
 *   options.ownAmendmentsFp           Amendments keep their own super projection fields
 *   options.currentAmendmentsFp       the scenario's current `amendments.fundProjections`
 *                                     (kept when the page rewrites the whole `amendments` map)
 *   options.seedAmendmentsFp          the amendment has no copy yet: `currentAmendmentsFp` is
 *                                     the SOA's (amendmentsProjectionRouting), written whole the
 *                                     first time, so no SOA field is lost from the copy
 *
 * → { scenario: { path: value }, shared: { path: value }, dropped: [path] }
 * Scenario paths are client-record paths (`recommendations.x`, `fees.y`, …);
 * a non-live scenario prefixes them with `payload.`.
 * The page lock fields are dropped: a scenario's lock is changed through the API.
 */
function routeFormDataUpdates(page, updates, options) {
  const o = options || {};
  const scenario = {};
  const shared = {};
  const dropped = [];
  const u = updates || {};
  Object.keys(u).forEach((path) => {
    const value = u[path];
    const dot = path.indexOf('.');
    const top = dot === -1 ? path : path.slice(0, dot);
    const rest = dot === -1 ? '' : path.slice(dot + 1);
    const child = rest ? rest.split('.')[0] : '';

    if (top === 'soaLocked' || top === 'roaEoLocked') {
      dropped.push(path);
      return;
    }

    if (page === 'recommendations') {
      if (top === 'recommendations') { scenario[path] = value; return; }
      if (top === 'fees') {
        if (!rest) {
          scenario.fees = scenarioFees(value);
          Object.keys(sharedFees(value)).forEach((key) => { shared['fees.' + key] = value[key]; });
          return;
        }
        if (isScenarioFeeKey(child)) scenario[path] = value; else shared[path] = value;
        return;
      }
      if (top === 'fundProjections') {
        if (!rest) {
          scenario.fundProjections = fundProjectionsAdvice(value);
          Object.keys(fundProjectionsShared(value)).forEach((key) => { shared['fundProjections.' + key] = value[key]; });
          return;
        }
        if (isFundProjectionsAdviceKey(child)) scenario[path] = value; else shared[path] = value;
        return;
      }
      shared[path] = value;
      return;
    }

    // Amendments
    if (top === 'amendments') {
      if (!rest && o.ownAmendmentsFp) {
        // The page never holds `amendments.fundProjections`; keep the stored one.
        const whole = Object.assign({}, isPlainObject(value) ? value : {});
        whole.fundProjections = cloneValue(isPlainObject(o.currentAmendmentsFp) ? o.currentAmendmentsFp : {});
        scenario.amendments = whole;
        return;
      }
      scenario[path] = value;
      return;
    }
    if (top === 'fundProjections' && o.ownAmendmentsFp) {
      if (!rest) {
        scenario['amendments.fundProjections'] = fundProjectionsAdvice(value);
        Object.keys(fundProjectionsShared(value)).forEach((key) => { shared['fundProjections.' + key] = value[key]; });
        return;
      }
      if (isFundProjectionsAdviceKey(child)) scenario['amendments.' + path] = value; else shared[path] = value;
      return;
    }
    shared[path] = value;
  });
  if (o.seedAmendmentsFp && o.ownAmendmentsFp) seedAmendmentsCopy(scenario, o.currentAmendmentsFp);
  return { scenario, shared, dropped };
}

/** The first write of an amendment's own projection: the SOA's copy with this write's fields set in it. */
function seedAmendmentsCopy(scenario, seed) {
  const prefix = 'amendments.fundProjections.';
  const childPaths = Object.keys(scenario).filter((path) => path.indexOf(prefix) === 0);
  if (!childPaths.length || Object.prototype.hasOwnProperty.call(scenario, 'amendments.fundProjections')) return;
  const whole = cloneValue(isPlainObject(seed) ? seed : {});
  childPaths.forEach((path) => {
    const keys = path.slice(prefix.length).split('.');
    let cursor = whole;
    keys.slice(0, -1).forEach((key) => {
      if (!isPlainObject(cursor[key])) cursor[key] = {};
      cursor = cursor[key];
    });
    cursor[keys[keys.length - 1]] = scenario[path];
    delete scenario[path];
  });
  scenario['amendments.fundProjections'] = whole;
}

/**
 * How an Amendments write routes the super projection (fixed Oct 27 2026): every amendment keeps
 * its own copy of the recommended / alternative projection fields. A client without one starts it
 * from the SOA's, so an Amendments rollover never rewrites the SOA's projection.
 * → routeFormDataUpdates options
 */
function amendmentsProjectionRouting(clientData) {
  if (hasOwnAmendmentsFundProjections(clientData)) {
    return { ownAmendmentsFp: true, currentAmendmentsFp: clientData.amendments.fundProjections, seedAmendmentsFp: false };
  }
  return { ownAmendmentsFp: true, currentAmendmentsFp: fundProjectionsAdvice((clientData && clientData.fundProjections) || {}), seedAmendmentsFp: true };
}

/** Prefix scenario paths for a non-live scenario document (`payload.recommendations.x`). */
function toPayloadPaths(scenarioUpdates) {
  const out = {};
  Object.keys(scenarioUpdates || {}).forEach((path) => { out['payload.' + path] = scenarioUpdates[path]; });
  return out;
}

// ---------- the index ----------

function emptyPageIndex() {
  return {
    rev: 0,
    liveId: LIVE_ALIAS,
    liveSince: null,
    items: [{
      id: LIVE_ALIAS, name: 'Scenario 1', locked: false, lockedReason: null, approval: null, sourceId: null,
    }],
  };
}

/** A client that has never used scenarios: one live, unlocked "Scenario 1" per page. */
function implicitIndex() {
  return { schemaVersion: 1, implicit: true, pages: { recommendations: emptyPageIndex(), amendments: emptyPageIndex() } };
}

function normaliseItem(item) {
  const it = isPlainObject(item) ? item : {};
  return {
    id: String(it.id || ''),
    name: String(it.name || ''),
    locked: it.locked === true,
    lockedReason: it.lockedReason || null,
    lockedAt: it.lockedAt || null,
    lockedBy: it.lockedBy || null,
    lockedByName: it.lockedByName || null,
    approval: isPlainObject(it.approval) ? it.approval : null,
    sourceId: it.sourceId || null,
    createdAt: it.createdAt || null,
    createdBy: it.createdBy || null,
    createdByName: it.createdByName || null,
  };
}

/** The stored index, or the implicit one when missing or malformed. */
function normaliseScenarioIndex(doc) {
  if (!isPlainObject(doc) || !isPlainObject(doc.pages)) return implicitIndex();
  const pages = {};
  PAGES.forEach((page) => {
    const p = isPlainObject(doc.pages[page]) ? doc.pages[page] : null;
    const items = p && Array.isArray(p.items) ? p.items.map(normaliseItem).filter((it) => it.id) : [];
    if (!p || !items.length) {
      pages[page] = emptyPageIndex();
      return;
    }
    const liveId = items.some((it) => it.id === p.liveId) ? p.liveId : items[0].id;
    pages[page] = { rev: Number(p.rev) || 0, liveId, liveSince: p.liveSince || null, items };
  });
  return { schemaVersion: Number(doc.schemaVersion) || 1, implicit: false, pages };
}

function findItem(pageIndex, id) {
  return ((pageIndex && pageIndex.items) || []).find((it) => it.id === id) || null;
}

// ---------- names ----------

function normaliseScenarioName(name) {
  return String(name == null ? '' : name).replace(/\s+/g, ' ').trim().slice(0, MAX_NAME_LENGTH);
}

function nameTaken(items, name, exceptId) {
  const n = normaliseScenarioName(name).toLowerCase();
  return (items || []).some((it) => (exceptId == null || it.id !== exceptId) && String(it.name || '').toLowerCase() === n);
}

/** "Scenario N", the first number not already used. */
function nextScenarioName(items) {
  for (let n = (items || []).length + 1; n < 100; n += 1) {
    const name = 'Scenario ' + n;
    if (!nameTaken(items, name)) return name;
  }
  return 'Scenario';
}

/** "Copy of X", or "Copy of X (2)" when taken. */
function copyScenarioName(sourceName, items) {
  const base = normaliseScenarioName('Copy of ' + (sourceName || 'scenario')).slice(0, MAX_NAME_LENGTH - 4);
  if (!nameTaken(items, base)) return base;
  for (let n = 2; n < 100; n += 1) {
    const name = base + ' (' + n + ')';
    if (!nameTaken(items, name)) return name;
  }
  return base;
}

module.exports = {
  PAGES,
  MAX_SCENARIOS,
  MAX_NAME_LENGTH,
  SHARED_FEE_KEYS,
  SCENARIO_ID_RE,
  LIVE_ALIAS,
  isPage,
  isPlainObject,
  cloneValue,
  isFundProjectionsAdviceKey,
  isScenarioFeeKey,
  fundProjectionsAdvice,
  fundProjectionsShared,
  scenarioFees,
  sharedFees,
  hasOwnAmendmentsFundProjections,
  amendmentsProjectionRouting,
  amendmentsAdviceView,
  extractScenarioPayload,
  applyScenarioPayload,
  buildLiveSwapUpdates,
  CLEARED_RECOMMENDATIONS,
  buildAdviceClearUpdates,
  routeFormDataUpdates,
  toPayloadPaths,
  implicitIndex,
  normaliseScenarioIndex,
  findItem,
  normaliseScenarioName,
  nameTaken,
  nextScenarioName,
  copyScenarioName,
};
